import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  createAcqMsg,
  deleteAcqMsg,
  deleteAcqMsgByMember,
  getAcqMsgList,
  updateAcqMsg,
} from '@shared/api/acqMsg/acqMsg.controller';
import { AcqMsg } from '@shared/api/acqMsg/acqMsg.interface';
import { Icon } from '@shared/components/icons';
import Modal from '@shared/components/Modal';
import ResizableBox from '@shared/components/ResizableBox';
import { simpleOpenAtomFamily } from '@shared/state/atom';

const PAGE_SIZE = 5;
const PREVIEW_COUNT = 3;

const CONDOLENCE_PHRASES = [
  '삼가 故人의 冥福을 빕니다',
  '삼가 고인의 명복을 빕니다',
  '천국에서 편히 쉬소서',
  '주님의 위로가 함께 하소서',
  '극락왕생발원',
];

interface Props {
  feventId: string;
  memberId?: string;
  bugoBrandName?: string;
}

const ACQ_MSG_FORM_MODAL_ID = 'acqMsgFormModal';

export const AcqMsgSection: FC<Props> = function AcqMsgSection({
  feventId,
  memberId,
  bugoBrandName,
}) {
  const { ChevronDown, ChevronUp, ChevronRight, Pencil } = Icon();
  const [messages, setMessages] = useState<AcqMsg[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const setFormModalOpen = useSetRecoilState(simpleOpenAtomFamily(ACQ_MSG_FORM_MODAL_ID));

  const fetchMessages = useCallback(async () => {
    try {
      const data = await getAcqMsgList(feventId);
      setMessages(data.filter((m) => !m.isBan));
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [feventId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const brandFixedMsg: AcqMsg | null = useMemo(() => {
    if (!bugoBrandName) return null;
    return {
      _id: '__brand__',
      name: `${bugoBrandName} 임직원 일동`,
      phoneNumber: '',
      message: '삼가 고인의 명복을 빕니다.',
      password: '',
      fevent: feventId,
      createdAt: '',
      updatedAt: '',
    };
  }, [bugoBrandName, feventId]);

  const allMessages = useMemo(() => {
    if (brandFixedMsg) return [...messages, brandFixedMsg];
    return messages;
  }, [messages, brandFixedMsg]);

  const totalPages = Math.max(1, Math.ceil(allMessages.length / PAGE_SIZE));

  const pagedMessages = useMemo(() => {
    const start = page * PAGE_SIZE;
    return allMessages.slice(start, start + PAGE_SIZE);
  }, [allMessages, page]);

  useEffect(() => {
    if (page >= totalPages) setPage(Math.max(0, totalPages - 1));
  }, [totalPages, page]);

  if (loading) return null;

  return (
    <div className="bg-white px-2">
      <div
        className="flex cursor-pointer items-center justify-between py-2"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-1">
          <Pencil className="wh-8" />
          <p className="font-bold text-gray-500">조의글 남기기</p>
        </div>
        <div className="cursor-pointer">{expanded ? <ChevronUp /> : <ChevronDown />}</div>
      </div>

      {!expanded && allMessages.length > 0 && (
        <div className="space-y-2 px-2 pb-2">
          {allMessages.slice(0, PREVIEW_COUNT).map((msg) => (
            <AcqMsgItem
              key={msg._id}
              msg={msg}
              memberId={memberId}
              onRefresh={fetchMessages}
            />
          ))}
          <div className="flex items-center justify-between border-t border-gray-100 pt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-gray-600 underline underline-offset-2 transition-colors hover:text-gray-900"
              onClick={() => setFormModalOpen(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="h-3.5 w-3.5"
              >
                <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.22 10.306a1 1 0 0 0-.26.445l-.813 3.04a.5.5 0 0 0 .607.607l3.04-.813a1 1 0 0 0 .445-.26l7.793-7.793a1.75 1.75 0 0 0 0-2.475l-.544-.544Z" />
              </svg>
              조의글 작성하기
            </button>
            {allMessages.length > PREVIEW_COUNT && (
              <button
                type="button"
                className="flex items-center gap-0.5 text-sm text-gray-600 transition-colors hover:text-gray-900"
                onClick={() => setExpanded(true)}
              >
                더보기
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <ResizableBox duration={0.3}>
        {expanded && (
          <div className="space-y-2 px-2 pb-3">
            {allMessages.length === 0 && (
              <p className="py-2 text-center text-sm text-gray-400">
                아직 조의글이 없습니다
              </p>
            )}

            <SwipeableArea
              onSwipeLeft={() => {
                if (page < totalPages - 1) setPage((p) => p + 1);
              }}
              onSwipeRight={() => {
                if (page > 0) setPage((p) => p - 1);
              }}
            >
              {pagedMessages.map((msg) => (
                <AcqMsgItem
                  key={msg._id}
                  msg={msg}
                  memberId={memberId}
                  onRefresh={fetchMessages}
                />
              ))}
            </SwipeableArea>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1 pt-1">
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 disabled:opacity-30"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronRight className="h-4 w-4 rotate-180" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`h-1.5 rounded-full transition-all ${
                      i === page ? 'w-4 bg-gray-500' : 'w-1.5 bg-gray-200'
                    }`}
                    onClick={() => setPage(i)}
                  />
                ))}
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 disabled:opacity-30"
                  disabled={page === totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-gray-100 pt-2">
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-gray-600 underline underline-offset-2 transition-colors hover:text-gray-900"
                onClick={() => setFormModalOpen(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="h-3.5 w-3.5"
                >
                  <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.22 10.306a1 1 0 0 0-.26.445l-.813 3.04a.5.5 0 0 0 .607.607l3.04-.813a1 1 0 0 0 .445-.26l7.793-7.793a1.75 1.75 0 0 0 0-2.475l-.544-.544Z" />
                </svg>
                조의글 작성하기
              </button>
              <button
                type="button"
                className="flex items-center gap-0.5 text-sm text-gray-600 transition-colors hover:text-gray-900"
                onClick={() => setExpanded(false)}
              >
                접기
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </ResizableBox>

      <Modal openId={ACQ_MSG_FORM_MODAL_ID} position="start" className="w-full max-w-md">
        <h3 className="mb-4 text-base font-bold text-gray-700">조의글 남기기</h3>
        <AcqMsgForm
          feventId={feventId}
          onSuccess={() => {
            setFormModalOpen(false);
            setPage(0);
            fetchMessages();
            history.back();
          }}
          onCancel={() => {
            setFormModalOpen(false);
            history.back();
          }}
        />
      </Modal>
    </div>
  );
};

const AcqMsgItem: FC<{
  msg: AcqMsg;
  memberId?: string;
  onRefresh: () => void;
}> = ({ msg, memberId, onRefresh }) => {
  const isFixed = msg._id === '__brand__';

  const [showActions, setShowActions] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [actionMode, setActionMode] = useState<'edit' | 'delete' | null>(null);
  const [editName, setEditName] = useState(msg.name);
  const [editMessage, setEditMessage] = useState(msg.message);
  const [error, setError] = useState('');

  const timeAgo = useMemo(() => {
    if (!msg.createdAt) return '';
    const diff = Date.now() - new Date(msg.createdAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '방금 전';
    if (mins < 60) return `${mins}분 전`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}시간 전`;
    const days = Math.floor(hours / 24);
    return `${days}일 전`;
  }, [msg.createdAt]);

  const handlePasswordAction = async () => {
    setError('');
    try {
      if (actionMode === 'delete') {
        await deleteAcqMsg(msg._id, passwordInput);
      } else if (actionMode === 'edit') {
        await updateAcqMsg(msg._id, {
          password: passwordInput,
          name: editName,
          message: editMessage,
        });
      }
      setActionMode(null);
      setPasswordInput('');
      onRefresh();
    } catch {
      setError('비밀번호가 일치하지 않습니다');
    }
  };

  const handleMemberDelete = async () => {
    if (!memberId) return;
    const confirmed = window.confirm(
      '상주님께서 조의글을 직접 관리(삭제)할 수 있는 기능입니다.\n해당 조의글을 삭제하시겠습니까?',
    );
    if (!confirmed) return;
    try {
      await deleteAcqMsgByMember(msg._id, memberId);
      onRefresh();
    } catch {
      setError('삭제에 실패했습니다');
    }
  };

  if (msg.isSecret && !memberId) {
    return (
      <div className="rounded-md bg-gray-50 px-3 py-2">
        <p className="text-sm text-gray-400">🔒 비밀글입니다</p>
      </div>
    );
  }

  return (
    <div className="rounded-md bg-gray-50 px-3 py-2.5">
      {msg.isSecret && memberId && (
        <p className="mb-1 text-xs text-gray-400">🔒 비밀글</p>
      )}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          {actionMode === 'edit' ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="보내시는 분"
                  className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm font-semibold"
                />
                {timeAgo && (
                  <span className="shrink-0 text-xs text-gray-400">{timeAgo}</span>
                )}
              </div>
              <textarea
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                className="w-full rounded border border-gray-300 p-1.5 text-sm"
                rows={2}
              />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-600">{msg.name}</span>
                {timeAgo && (
                  <span className="shrink-0 text-xs text-gray-400">{timeAgo}</span>
                )}
              </div>
              <p className="mt-0.5 whitespace-pre-line break-keep text-sm leading-relaxed text-gray-600">
                {msg.message}
              </p>
            </>
          )}
        </div>
        {!isFixed && !actionMode && (
          <button
            className="ml-2 flex shrink-0 items-center justify-center rounded-full p-1 text-gray-500 active:bg-gray-200"
            onClick={() => setShowActions(!showActions)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              fill="currentColor"
              className="h-4 w-4"
            >
              <path d="M8 2a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM8 6.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM9.5 12.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z" />
            </svg>
          </button>
        )}
      </div>

      {showActions && !actionMode && (
        <div className="mt-1.5 flex justify-end gap-2">
          <button
            className="text-xs text-gray-500 underline"
            onClick={() => {
              setActionMode('edit');
              setShowActions(false);
            }}
          >
            수정
          </button>
          <button
            className="text-xs text-gray-500 underline"
            onClick={() => {
              setActionMode('delete');
              setShowActions(false);
            }}
          >
            삭제
          </button>
          {memberId && (
            <button
              className="text-xs text-red-400 underline"
              onClick={handleMemberDelete}
            >
              관리 삭제
            </button>
          )}
        </div>
      )}

      {actionMode && (
        <div className="mt-2 space-y-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder="비밀번호 4자리"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value.replace(/\D/g, ''))}
              className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
            <button
              className="shrink-0 rounded bg-gray-700 px-3 py-1.5 text-sm text-white disabled:opacity-40"
              onClick={handlePasswordAction}
              disabled={passwordInput.length !== 4}
            >
              {actionMode === 'edit' ? '수정' : '삭제'}
            </button>
            <button
              className="shrink-0 text-sm text-gray-400"
              onClick={() => {
                setActionMode(null);
                setPasswordInput('');
                setError('');
                setEditName(msg.name);
                setEditMessage(msg.message);
              }}
            >
              취소
            </button>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )}
    </div>
  );
};

const AcqMsgForm: FC<{
  feventId: string;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ feventId, onSuccess, onCancel }) => {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPhrases, setShowPhrases] = useState(false);

  const canSubmit =
    name.trim() && phoneNumber.trim() && message.trim() && password.length === 4;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await createAcqMsg({
        name: name.trim(),
        phoneNumber: phoneNumber.trim(),
        message: message.trim(),
        password,
        fevent: feventId,
        isSecret,
      });
      onSuccess();
    } catch {
      setError('조의글 등록에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none';

  return (
    <div className="w-full space-y-3">
      <input
        className={inputClass}
        placeholder="보내시는 분 (예: ○○회사 대표 홍길동)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className={inputClass}
        placeholder="휴대번호(수정 및 답례 용도)"
        inputMode="tel"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
      />

      <div>
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="mb-1 text-xs text-gray-400 underline"
            onClick={() => setShowPhrases(!showPhrases)}
          >
            {showPhrases ? '추천 문구 닫기' : '추천 문구'}
          </button>
        </div>
        {showPhrases && (
          <div className="mb-2 flex flex-wrap gap-1">
            {CONDOLENCE_PHRASES.map((phrase) => (
              <button
                key={phrase}
                type="button"
                className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600"
                onClick={() => {
                  setMessage(phrase);
                  setShowPhrases(false);
                }}
              >
                {phrase}
              </button>
            ))}
          </div>
        )}
        <textarea
          className={`${inputClass} resize-none`}
          placeholder="조의글을 남겨주세요"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>

      <div className="flex min-w-0 items-center gap-2">
        <input
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
          type="password"
          inputMode="numeric"
          maxLength={4}
          placeholder="비밀번호 4자리"
          value={password}
          onChange={(e) => setPassword(e.target.value.replace(/\D/g, ''))}
        />
        <label className="flex shrink-0 items-center gap-1 text-xs text-gray-500">
          <input
            type="checkbox"
            checked={isSecret}
            onChange={(e) => setIsSecret(e.target.checked)}
          />
          비밀글
        </label>
      </div>
      <p className="text-xs text-gray-400">비밀번호는 수정/삭제 시 필요합니다</p>

      {error && <p className="text-xs text-red-500">{error}</p>}

      <div className="flex min-w-0 gap-2 pt-1">
        <button
          type="button"
          className="shrink-0 rounded-md border border-gray-300 px-4 py-2.5 text-sm text-gray-500"
          onClick={onCancel}
        >
          취소
        </button>
        <button
          type="button"
          className="min-w-0 flex-1 rounded-md bg-gray-800 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? '등록 중...' : '등록'}
        </button>
      </div>
    </div>
  );
};

const SWIPE_THRESHOLD = 50;

const SwipeableArea: FC<{
  children: React.ReactNode;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}> = ({ children, onSwipeLeft, onSwipeRight }) => {
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  return (
    <div
      className="space-y-2"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0]?.clientX ?? 0;
        touchEndX.current = e.touches[0]?.clientX ?? 0;
      }}
      onTouchMove={(e) => {
        touchEndX.current = e.touches[0]?.clientX ?? 0;
      }}
      onTouchEnd={() => {
        const diff = touchStartX.current - touchEndX.current;
        if (diff > SWIPE_THRESHOLD) onSwipeLeft();
        else if (diff < -SWIPE_THRESHOLD) onSwipeRight();
      }}
    >
      {children}
    </div>
  );
};
